/**
 * Level progress bar showing Lv1-Lv5 circles connected by separators.
 *
 * Level states (driven by BE `status` field):
 * - completed / in_progress / not_started — normal circle styles, in_progress wearing the orange ring.
 * - skipped — pill with white bg + brand border/text (skipped at assessment, not yet learned).
 * - skipped_completed — pill with brand bg + white text (skipped at assessment, later learned and passed).
 */

import { FC, memo } from "react";
import { Stack, Typography } from "@mui/material";
import { background, divider } from "@/theme/colors";
import { TCurriculumLevelDto, TLevelStatusDto } from "@/api/services/main/curriculum-progress/dtos/get-curriculum-progress.dto";
import { isLevelDoneStatus, isSkipStatus } from "@/features/dashboard/utils/curriculumLevel";
import {
  LEVEL_COMPLETED_STYLES,
  LEVEL_DISABLED_STYLES,
  LEVEL_IN_PROGRESS_STYLES,
  LEVEL_NOT_STARTED_STYLES,
  LEVEL_PROGRESS_BAR_SIZES,
  LEVEL_SKIP_LEARNED_STYLES,
  LEVEL_SKIP_PENDING_STYLES,
  TLevelStyles,
} from "@/features/dashboard/constants/levelProgressBar";

type TLevelProgressBarProps = {
  levels: TCurriculumLevelDto[];
  /** When true, all levels render in disabled (greyed out) state */
  disabled?: boolean;
};

const LEVEL_NUMBER_REGEX = /(\d+)/;

const formatLevelName = (levelKey: string): string => {
  const match = levelKey.match(LEVEL_NUMBER_REGEX);
  return match ? `Lv${match[1]}` : levelKey;
};

const getLevelStyles = (status: TLevelStatusDto): TLevelStyles => {
  switch (status) {
    case "skipped":
      return LEVEL_SKIP_PENDING_STYLES;
    case "skipped_completed":
      return LEVEL_SKIP_LEARNED_STYLES;
    case "completed":
      return LEVEL_COMPLETED_STYLES;
    case "in_progress":
      return LEVEL_IN_PROGRESS_STYLES;
    default:
      return LEVEL_NOT_STARTED_STYLES;
  }
};

const getSeparatorColor = (current: TCurriculumLevelDto, next: TCurriculumLevelDto): string => {
  if (isLevelDoneStatus(current.status) && (isLevelDoneStatus(next.status) || next.status === "in_progress")) {
    return background.brand;
  }
  return divider.high;
};

type TLevelItemProps = {
  level: TCurriculumLevelDto;
  nextLevel?: TCurriculumLevelDto;
  disabled: boolean;
  isLast: boolean;
};

const LevelItem: FC<TLevelItemProps> = memo(({ level, nextLevel, disabled, isLast }) => {
  const styles = disabled ? LEVEL_DISABLED_STYLES : getLevelStyles(level.status);
  const showSkipLabel = !disabled && isSkipStatus(level.status);

  return (
    <Stack direction="row" alignItems="center" flex={isLast ? undefined : 1}>
      <Stack
        role="img"
        aria-label={showSkipLabel ? `${formatLevelName(level.level)} スキップ済み` : formatLevelName(level.level)}
        sx={{
          width: LEVEL_PROGRESS_BAR_SIZES.circle,
          height: showSkipLabel ? LEVEL_PROGRESS_BAR_SIZES.pill : LEVEL_PROGRESS_BAR_SIZES.circle,
          borderRadius: showSkipLabel ? "999px" : "50%",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          ...styles,
        }}
      >
        <Typography variant="sub16Bold" color={styles.color} aria-hidden>
          {formatLevelName(level.level)}
        </Typography>
        {showSkipLabel && (
          <Typography variant="cap12" color={styles.color} aria-hidden>
            SKIP
          </Typography>
        )}
      </Stack>

      {!isLast && nextLevel && (
        <Stack
          height={LEVEL_PROGRESS_BAR_SIZES.separatorHeight}
          flexGrow={1}
          minWidth={LEVEL_PROGRESS_BAR_SIZES.separatorMinWidth}
          bgcolor={disabled ? divider.high : getSeparatorColor(level, nextLevel)}
          flexShrink={0}
        />
      )}
    </Stack>
  );
});
LevelItem.displayName = "LevelItem";

export const LevelProgressBar: FC<TLevelProgressBarProps> = memo(({ levels, disabled = false }) => (
  <Stack direction="row" alignItems="center" justifyContent="center">
    {levels.map((level, index) => (
      <LevelItem key={level.level} level={level} nextLevel={levels[index + 1]} disabled={disabled} isLast={index === levels.length - 1} />
    ))}
  </Stack>
));
LevelProgressBar.displayName = "LevelProgressBar";
